import React, { useState } from 'react';
import { useLocation } from 'react-router';

import BasicLayout from 'components/BasicLayout';
import EmptyList from 'components/EmptyList';
import MenuList from 'components/MenuList';
import MenuListItem from 'components/MenuList/MenuListItem';
import { Desktop, Mobile } from 'components/Responsive';
import TripCard, { ShimmerTripCard } from 'components/TripCard';
import { HelpLink } from 'components/TripCard/styles';
import TripListContainer from 'containers/TripList';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { faq as faqSchema } from 'i18n/faq/en-us.json';
import { TravelForCustomerSupport } from 'models/customerSupport';

import ChannelsFloatingMenu from '../../components/ChannelsFloatingMenu';
import { mapCustomerSupportTravel } from '../../utils/mapper';
import Contact from '../Contact';
import { SectionTitle, HeaderWithLink, AllTripsLink, TripCarousel, TripCardWrapper } from './styles';

type TripListProps = {
  travels: TravelForCustomerSupport[];
  totalCount: number;
  detailsOpen: boolean;
  onToggle: (status: boolean) => void;
};

const TripList = ({ travels, totalCount, detailsOpen, onToggle }: TripListProps) => {
  const { t } = useStoreTranslation();

  return travels.length > 0 ? (
    <TripCarousel options={{ gap: 16, arrows: false }}>
      {[...Array(totalCount).keys()].map((index) => {
        const travel = travels[index];

        return (
          <TripCardWrapper key={index}>
            {travel ? (
              <TripCard travel={mapCustomerSupportTravel(travel)} onToggle={onToggle} detailsOpen={detailsOpen}>
                <HelpLink to={`/customerSupport/trip/${travel.travelId}`}>{t('customerSupport.mobileTitle')}</HelpLink>
              </TripCard>
            ) : (
              <ShimmerTripCard />
            )}
          </TripCardWrapper>
        );
      })}
    </TripCarousel>
  ) : (
    <EmptyList
      transparent
      compact
      title={t('customerSupport.home.tripList.empty.title')}
      description={t('customerSupport.home.tripList.empty.description')}
      paddingMessage={48}
      image="/images/receipt-list-43-colored-48.svg"
    />
  );
};

const Home = () => {
  const { t } = useStoreTranslation();
  const location = useLocation<{ channelsOpen?: boolean }>();
  const [detailsOpen, setDetailsOpen] = useState(false);

  return (
    <>
      <Mobile>
        <BasicLayout withFloatingButton backPath="/dashboard" title={t('customerSupport.title')}>
          <HeaderWithLink>
            <SectionTitle>{t('customerSupport.tripSupportTitle')}</SectionTitle>
            <AllTripsLink to="/customerSupport/allTrips">{t('customerSupport.viewAllTrips')}</AllTripsLink>
          </HeaderWithLink>

          <TripListContainer
            render={(loading, { travels, totalCount }) =>
              loading ? (
                <TripCardWrapper>
                  <ShimmerTripCard />
                </TripCardWrapper>
              ) : (
                <TripList
                  travels={travels}
                  totalCount={totalCount}
                  detailsOpen={detailsOpen}
                  onToggle={(status) => setDetailsOpen(status)}
                />
              )
            }
          />

          <SectionTitle>{t('customerSupport.faq.title')}</SectionTitle>
          <MenuList>
            {Object.keys(faqSchema).map((section) => (
              <MenuListItem
                key={section}
                to={`/customerSupport/faq/${section}`}
                label={t(`faq.${section}.title`)}
              ></MenuListItem>
            ))}
          </MenuList>

          <ChannelsFloatingMenu defaultOpen={location.state?.channelsOpen} />
        </BasicLayout>
      </Mobile>
      <Desktop>
        <Contact />
      </Desktop>
    </>
  );
};

export default Home;
